/**
 * MHTML(RFC 2557 multipart/related 单文件网页归档)的纯前端解析与生成。
 *
 * 为什么在前端做:浏览器对 HTTP 下发的 .mhtml 只会触发下载、不渲染
 * (只有 file:// 本地打开才认),所以「打开」必须自己解 MIME:解出根
 * HTML 与资源部件(图片/CSS),把 Content-Location / cid: 引用改写成
 * data URL,得到一份可直接塞进沙箱 iframe 的独立 HTML。
 *
 * 「生成」走反方向:把报告 DOM 序列化成 multipart/related(HTML 根 +
 * 图片部件),Chrome/Edge/Word 都能直接打开 —— Jarod 的博颂汇总报告
 * 就是这种格式(比纯 html 多了内嵌图片)。
 */

// ── 解析 ────────────────────────────────────────────────────────────

interface MhtmlPart {
  mime: string;
  charset: string;
  encoding: string;
  location: string;
  contentId: string;
  rawBody: string; // latin1 视角的原始字节串
}

const latin1 = (buf: ArrayBuffer) => new TextDecoder("latin1").decode(buf);

function headerValue(headers: string, name: string): string {
  // 头部可能折行(RFC 822 continuation:后续行以空白开头)
  const re = new RegExp(`^${name}:[ \\t]*([^\\r\\n]*(?:\\r?\\n[ \\t]+[^\\r\\n]*)*)`, "im");
  const m = headers.match(re);
  return m ? m[1]!.replace(/\r?\n[ \t]+/g, " ").trim() : "";
}

function mimeOf(contentType: string): string {
  return (contentType.split(";")[0] ?? "").trim().toLowerCase();
}

function paramOf(contentType: string, param: string): string {
  const m = contentType.match(new RegExp(`${param}\\s*=\\s*"?([^";\\r\\n]+)"?`, "i"));
  return m ? m[1]!.trim() : "";
}

function decodeQuotedPrintable(s: string): string {
  return s
    .replace(/=\r?\n/g, "") // 软换行
    .replace(/=([0-9A-Fa-f]{2})/g, (_, h: string) => String.fromCharCode(parseInt(h, 16)));
}

/** 部件体 → 字节数组(latin1 字符串的每个 charCode 即一字节)。 */
function bodyBytes(part: MhtmlPart): Uint8Array {
  const enc = part.encoding.toLowerCase();
  let raw = part.rawBody;
  if (enc === "base64") {
    raw = atob(raw.replace(/\s+/g, ""));
  } else if (enc === "quoted-printable") {
    raw = decodeQuotedPrintable(raw);
  }
  return Uint8Array.from(raw, (ch) => ch.charCodeAt(0) & 0xff);
}

function bytesToBase64(bytes: Uint8Array): string {
  let bin = "";
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    bin += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(bin);
}

/** RFC 2047 encoded-word(=?charset?B/Q?...?=)解码;非编码词原样返回。
 *  Chrome 存的 mhtml Subject 用它编 CJK。 */
function decodeRfc2047(s: string): string {
  // 相邻 encoded-word 间空白按规范丢弃;多字节字符可能跨词拆分 ——
  // 同一串相邻词先合并字节再统一解码。
  const merged = s.replace(/\?=[ \t\r\n]+=\?/g, "?==?");
  return merged.replace(/(?:=\?[^?]+\?[BbQq]\?[^?]*\?=)+/g, (run) => {
    const bytes: number[] = [];
    let charset = "utf-8";
    for (const w of run.match(/=\?([^?]+)\?([BbQq])\?([^?]*)\?=/g) ?? []) {
      const m = w.match(/=\?([^?]+)\?([BbQq])\?([^?]*)\?=/)!;
      charset = m[1]!;
      const raw = m[2]!.toUpperCase() === "B"
        ? atob(m[3]!)
        : decodeQuotedPrintable(m[3]!.replace(/_/g, " "));
      for (let i = 0; i < raw.length; i++) bytes.push(raw.charCodeAt(i) & 0xff);
    }
    try {
      return new TextDecoder(charset).decode(new Uint8Array(bytes));
    } catch {
      return run;
    }
  });
}

function escapeRe(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** 解析 .mhtml 字节 → 可直接渲染的独立 HTML(资源已内联为 data URL)。 */
export function parseMhtml(buf: ArrayBuffer): { html: string; title: string } {
  const whole = latin1(buf);
  const headerEnd = whole.search(/\r?\n\r?\n/);
  if (headerEnd < 0) throw new Error("not an MHTML file (no header block)");
  const topHeaders = whole.slice(0, headerEnd);
  const contentType = headerValue(topHeaders, "Content-Type");
  const boundary = paramOf(contentType, "boundary");
  const title = decodeRfc2047(headerValue(topHeaders, "Subject")) || "MHTML";

  let parts: MhtmlPart[] = [];
  if (!boundary) {
    // 退化情况:单部件(无 multipart)—— 整个 body 当 HTML
    parts = [{
      mime: mimeOf(contentType) || "text/html",
      charset: paramOf(contentType, "charset") || "utf-8",
      encoding: headerValue(topHeaders, "Content-Transfer-Encoding") || "8bit",
      location: "", contentId: "",
      rawBody: whole.slice(headerEnd).replace(/^\r?\n\r?\n/, ""),
    }];
  } else {
    const segments = whole.split(new RegExp(`\\r?\\n--${escapeRe(boundary)}(?:--)?[ \\t]*(?=\\r?\\n|$)`));
    for (const seg of segments.slice(1)) {
      const s = seg.replace(/^\r?\n/, "");
      const he = s.search(/\r?\n\r?\n/);
      if (he < 0) continue;
      const h = s.slice(0, he);
      const ct = headerValue(h, "Content-Type");
      if (!ct) continue;
      parts.push({
        mime: mimeOf(ct),
        charset: paramOf(ct, "charset") || "utf-8",
        encoding: headerValue(h, "Content-Transfer-Encoding") || "8bit",
        location: headerValue(h, "Content-Location"),
        contentId: headerValue(h, "Content-ID").replace(/^<|>$/g, ""),
        rawBody: s.slice(he).replace(/^\r?\n\r?\n?/, ""),
      });
    }
  }

  const rootIdx = parts.findIndex((p) => p.mime === "text/html");
  if (rootIdx < 0) throw new Error("MHTML has no text/html root part");
  const root = parts[rootIdx]!;

  const textOf = (p: MhtmlPart) => {
    try {
      return new TextDecoder(p.charset || "utf-8").decode(bodyBytes(p));
    } catch {
      return new TextDecoder("utf-8").decode(bodyBytes(p));
    }
  };

  // 资源引用键 → data URL。键含 Content-Location(完整 + 无 scheme 变体)
  // 与 cid:。CSS 文本部件先把自己内部的引用改写完再编码。
  const resources = parts.filter((_, i) => i !== rootIdx && parts[i]!.mime !== "text/html");
  const keyToData = new Map<string, string>();
  const register = (p: MhtmlPart, dataUrl: string) => {
    if (p.location) {
      keyToData.set(p.location, dataUrl);
      keyToData.set(p.location.replace(/^https?:\/\//, "//"), dataUrl);
    }
    if (p.contentId) keyToData.set(`cid:${p.contentId}`, dataUrl);
  };
  const rewriteRefs = (text: string): string => {
    let out = text;
    const keys = [...keyToData.keys()].sort((a, b) => b.length - a.length);
    for (const k of keys) out = out.split(k).join(keyToData.get(k)!);
    return out;
  };

  // 分轮注册:二进制 → CSS(内部可引用图片)→ 子帧 HTML(iframe 型
  // 归档,内部可引用前两类)→ 最后根 HTML。
  const subHtml = parts.filter((p, i) => i !== rootIdx && p.mime === "text/html");
  for (const p of resources) {
    if (p.mime === "text/css") continue;
    register(p, `data:${p.mime};base64,${bytesToBase64(bodyBytes(p))}`);
  }
  for (const p of resources) {
    if (p.mime !== "text/css") continue;
    const css = rewriteRefs(textOf(p));
    register(p, `data:text/css;base64,${bytesToBase64(new TextEncoder().encode(css))}`);
  }
  for (const p of subHtml) {
    const inner = rewriteRefs(textOf(p));
    register(p, `data:text/html;base64,${bytesToBase64(new TextEncoder().encode(inner))}`);
  }
  const html = rewriteRefs(textOf(root));
  return { html, title };
}

// ── 生成 ────────────────────────────────────────────────────────────

const BOUNDARY = "----=_NovaSchool_MHTML_Boundary";
const LOC_BASE = "https://nova-school.report/";

function wrap76(b64: string): string {
  return b64.replace(/(.{76})/g, "$1\r\n");
}

interface ResourcePart {
  name: string;
  mime: string;
  base64: string;
}

export function buildMhtml(title: string, html: string, resources: ResourcePart[]): Blob {
  const htmlB64 = bytesToBase64(new TextEncoder().encode(html));
  const head =
    `From: <Saved by Nova School>\r\n` +
    `Subject: =?utf-8?B?${bytesToBase64(new TextEncoder().encode(title.replace(/[\r\n]/g, " ")))}?=\r\n` +
    `MIME-Version: 1.0\r\n` +
    `Content-Type: multipart/related; type="text/html"; boundary="${BOUNDARY}"\r\n\r\n`;
  let body =
    `--${BOUNDARY}\r\n` +
    `Content-Type: text/html; charset="utf-8"\r\n` +
    `Content-Transfer-Encoding: base64\r\n` +
    `Content-Location: ${LOC_BASE}index.html\r\n\r\n` +
    `${wrap76(htmlB64)}\r\n`;
  for (const r of resources) {
    body +=
      `--${BOUNDARY}\r\n` +
      `Content-Type: ${r.mime}\r\n` +
      `Content-Transfer-Encoding: base64\r\n` +
      `Content-Location: ${LOC_BASE}${r.name}\r\n\r\n` +
      `${wrap76(r.base64)}\r\n`;
  }
  body += `--${BOUNDARY}--\r\n`;
  return new Blob([head + body], { type: "multipart/related" });
}

/** 把一个已渲染的 DOM 节点(如报告卡片)序列化成 MHTML。
 *  - 同源样式表全文并入 <style>(Tailwind 一张表,离线打开样式不丢)
 *  - <img> 抓字节转部件;<canvas> 转 PNG 部件;内联 SVG 原样保留 */
export async function domToMhtml(el: HTMLElement, title: string): Promise<Blob> {
  const clone = el.cloneNode(true) as HTMLElement;
  const resources: ResourcePart[] = [];
  let n = 0;

  const srcImgs = Array.from(el.querySelectorAll("img"));
  const cloneImgs = Array.from(clone.querySelectorAll("img"));
  for (let i = 0; i < cloneImgs.length; i++) {
    const img = cloneImgs[i]!;
    const src = srcImgs[i]?.getAttribute("src") ?? img.getAttribute("src") ?? "";
    if (!src) continue;
    try {
      if (src.startsWith("data:")) continue; // 已内联,直接保留
      const resp = await fetch(src);
      const blob = await resp.blob();
      const b64 = bytesToBase64(new Uint8Array(await blob.arrayBuffer()));
      const name = `image${n++}`;
      resources.push({ name, mime: blob.type || "image/png", base64: b64 });
      img.setAttribute("src", `${LOC_BASE}${name}`);
    } catch {
      /* 抓不到就保留原 src(在线打开仍可显示) */
    }
  }
  // canvas 在 clone 里是空白 —— 从原节点取像素
  const origCanvases = Array.from(el.querySelectorAll("canvas"));
  Array.from(clone.querySelectorAll("canvas")).forEach((cv, i) => {
    const orig = origCanvases[i];
    if (!orig) return;
    try {
      const dataUrl = orig.toDataURL("image/png");
      const imgEl = cv.ownerDocument.createElement("img");
      const name = `canvas${n++}.png`;
      resources.push({ name, mime: "image/png", base64: dataUrl.split(",")[1] ?? "" });
      imgEl.setAttribute("src", `${LOC_BASE}${name}`);
      imgEl.setAttribute("width", String(orig.width));
      imgEl.setAttribute("height", String(orig.height));
      cv.replaceWith(imgEl);
    } catch {
      /* tainted canvas 等,跳过 */
    }
  });

  let styles = "";
  for (const sheet of Array.from(document.styleSheets)) {
    try {
      styles += Array.from(sheet.cssRules).map((r) => r.cssText).join("\n") + "\n";
    } catch {
      /* 跨域样式表读不了,跳过 */
    }
  }

  const html =
    `<!doctype html><html><head><meta charset="utf-8">` +
    `<title>${title}</title><style>${styles}</style></head>` +
    `<body style="background:#fff;margin:24px auto;max-width:900px">${clone.outerHTML}</body></html>`;
  return buildMhtml(title, html, resources);
}

export const MHTML_EXTS = ["mht", "mhtml"];

/** 把 .mhtml 文件转成可入库检索的独立 .html 文件(镜像入「我的文件」
 *  时用):supernova 解析器不认 mhtml 但认 html。为了控制体积并聚焦
 *  正文,剥掉 <script> 与内联的 data: 图片(检索只要文字)。 */
export async function mhtmlFileToHtmlFile(file: File): Promise<File> {
  const { html } = parseMhtml(await file.arrayBuffer());
  // 激进瘦身:检索只要文字。样式块/脚本/注释/一切 data: 内联(含 CSS
  // url(data:...))全剥 —— 否则图片型 deck 会产出几 MB 的镜像文件。
  const slim = html
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<style[\s\S]*?<\/style>/gi, "")
    .replace(/<link[^>]*>/gi, "")
    .replace(/<!--[\s\S]*?-->/g, "")
    .replace(/url\((['"]?)data:[^)]*\1\)/gi, "url()")
    .replace(/src="data:[^"]*"/gi, 'src=""')
    .replace(/src='data:[^']*'/gi, "src=''")
    .replace(/(href|poster|background)="data:[^"]*"/gi, '$1=""');
  const base = file.name.replace(/\.(mht|mhtml)$/i, "");
  return new File([slim], `${base}.html`, { type: "text/html" });
}
